import { useEffect } from "react";
import { NAP, SITE_URL } from "@/data/site";

// Lightweight runtime SEO: sets title, meta description, canonical, OG/Twitter,
// and injects page-specific JSON-LD structured data. Best-effort for SPA.

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, path = "/", image, type = "website", jsonLd }) {
  const serializedJsonLd = JSON.stringify(jsonLd || []);
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img =
      (image ? new URL(image, SITE_URL).href : null) ||
      "https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?auto=format&fit=crop&w=1200&q=80";

    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index,follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // JSON-LD
    const parsedJsonLd = JSON.parse(serializedJsonLd);
    const blocks = Array.isArray(parsedJsonLd) ? parsedJsonLd : [parsedJsonLd];
    const nodes = [];
    blocks.forEach((block, i) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", `ld-${i}`);
      script.text = JSON.stringify(block);
      document.head.appendChild(script);
      nodes.push(script);
    });
    return () => nodes.forEach((n) => n.remove());
  }, [title, description, path, image, type, serializedJsonLd]);
}

export function localBusinessLd() {
  return {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: NAP.name,
    legalName: NAP.legalName,
    image: `${SITE_URL}/projects/new-home.jpg`,
    "@id": `${SITE_URL}/#business`,
    url: SITE_URL,
    telephone: NAP.phoneDisplay,
    email: NAP.email,
    priceRange: NAP.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: NAP.street,
      addressLocality: NAP.city,
      addressRegion: NAP.region,
      postalCode: NAP.postalCode,
      addressCountry: NAP.country,
    },
    geo: { "@type": "GeoCoordinates", latitude: NAP.geo.lat, longitude: NAP.geo.lng },
    areaServed: NAP.areaServed.map((a) => ({ "@type": "City", name: a })),
    openingHours: NAP.hours,
    sameAs: Object.values(NAP.social),
  };
}

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: NAP.name,
    url: SITE_URL,
    email: NAP.email,
    telephone: NAP.phoneDisplay,
    sameAs: Object.values(NAP.social),
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: NAP.name,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/work?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function serviceLd(serviceType, description) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    description,
    provider: { "@type": "GeneralContractor", name: NAP.name, url: SITE_URL },
    areaServed: NAP.areaServed.map((a) => ({ "@type": "City", name: a })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}
